import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { CampaignStockVinsService } from '../../../../core/services/campaign-stock-vins/campaign-stock-vins.service';
import { AlertService } from '../../../../core/services/alert/alert.service';
import { CampaignStockVin } from '../../../../core/interfaces/campaign-stock-vins';
import { BastidoresFormComponent } from './bastidores-form/bastidores-form.component';
import { BastidoresImportComponent } from './bastidores-import/bastidores-import.component';

@Component({
  selector: 'app-bastidores-tab',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MatPaginatorModule,
    BastidoresFormComponent,
    BastidoresImportComponent,
  ],
  templateUrl: './bastidores-tab.component.html',
  styleUrl: './bastidores-tab.component.css'
})
export class BastidoresTabComponent implements OnChanges {
  @Input() stockData: any;
  @Input() ladata: any = [];

  bastidores: CampaignStockVin[] = [];
  showCreateForm = false;
  showImportPanel = false;
  editingVin: CampaignStockVin | null = null;

  pageNumber = 1;
  pageSize = 25;
  total = 0;
  lastPage = 1;

  constructor(
    private campaignStockVinsService: CampaignStockVinsService,
    private alertService: AlertService
  ) { }

  get campaignStockId(): string {
    return this.stockData?.reference || '';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['stockData'] && this.campaignStockId) {
      this.loadBastidores();
    }
  }

  loadBastidores(): void {
    const params: any = { campaign_stock_id: this.campaignStockId };

    this.campaignStockVinsService.getVins(this.pageNumber, this.pageSize, params).subscribe({
      next: (response) => {
        this.bastidores = response.data || [];
        if (response.meta) {
          this.total = response.meta.total || 0;
          this.lastPage = response.meta.last_page || 1;
        }
      },
      error: (error) => {
        this.bastidores = [];
      },
    });
  }

  onCreateNew(): void {
    this.editingVin = null;
    this.showCreateForm = true;
    this.showImportPanel = false;
  }

  onEdit(vin: CampaignStockVin): void {
    this.editingVin = vin;
    this.showCreateForm = true;
    this.showImportPanel = false;
  }

  onSave(event: any): void {
    this.alertService.loading('swal_messages.please_wait');

    if (event.isEdit) {
      this.campaignStockVinsService.updateVin(event.reference, event.body).subscribe({
        next: () => {
          this.alertService.successOk('swal_messages.campaign_stock_vins.campaign_stock_vin_updated');
          this.showCreateForm = false;
          this.editingVin = null;
          this.loadBastidores();
        },
        error: (error) => {
          this.alertService.error(error.error);
        },
      });
    } else {
      this.campaignStockVinsService.createVin(event.body).subscribe({
        next: () => {
          this.alertService.successOk('swal_messages.campaign_stock_vins.campaign_stock_vin_created');
          this.showCreateForm = false;
          this.loadBastidores();
        },
        error: (error) => {
          this.alertService.error(error.error);
        },
      });
    }
  }

  onDelete(vin: CampaignStockVin): void {
    this.alertService
      .confirmation(
        'bastidores.confirm_delete',
        'bastidores.confirm_delete_message',
        'swal_messages.confirm',
        'swal_messages.back'
      )
      .then((result) => {
        if (result.isConfirmed) {
          this.alertService.loading('swal_messages.please_wait');
          this.campaignStockVinsService.deleteVin(vin.reference).subscribe({
            next: () => {
              this.alertService.successOk('swal_messages.campaign_stock_vins.campaign_stock_vin_deleted');
              this.loadBastidores();
            },
            error: (error) => {
              if (error.status === 409) {
                this.alertService.error('swal_messages.campaign_stock_vins.campaign_stock_vin_in_use');
              } else {
                this.alertService.error(error.error);
              }
            },
          });
        }
      });
  }

  toggleImport(): void {
    this.showImportPanel = !this.showImportPanel;
    this.showCreateForm = false;
  }

  onImportComplete(): void {
    this.showImportPanel = false;
    this.loadBastidores();
  }

  onExport(): void {
    const params: any = { campaign_stock_id: this.campaignStockId };

    this.alertService.loading('swal_messages.please_wait');
    this.campaignStockVinsService.exportVins(params).subscribe({
      next: (response) => {
        this.alertService.close();
        const url = response.data?.url;
        if (url) {
          const a = document.createElement('a');
          a.href = url;
          a.download = `bastidores-${this.campaignStockId}.xlsx`;
          a.target = '_blank';
          a.click();
        }
      },
      error: (error) => {
        this.alertService.error('swal_messages.campaign_stock_vins.export_failed');
      },
    });
  }

  onCancelForm(): void {
    this.showCreateForm = false;
    this.editingVin = null;
  }

  onPageChange(event: PageEvent): void {
    this.pageNumber = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadBastidores();
  }

  getStateBadgeClass(state: string | null): string {
    switch (state) {
      case 'factory': return 'text-new';
      case 'countryside': return 'text-draft';
      default: return 'text-no-action';
    }
  }
}
